import { z } from "zod";
import { GeoLayerType } from "../../constants/geo-layer-type.enum";

/**
 * Re-export GeoLayerType for convenience
 */
export { GeoLayerType };

/**
 * Schema for Leaflet CircleMarker style options
 * Used to validate current layer style when extracting hover state
 */
export const leafletCircleMarkerStyleSchema = z.object({
  radius: z.number().optional(),
  fillColor: z.string().optional(),
  fillOpacity: z.number().optional(),
  color: z.string().optional(),
  weight: z.number().optional(),
  opacity: z.number().optional(),
}).passthrough(); // Allow additional Leaflet properties

export type LeafletCircleMarkerStyle = z.infer<typeof leafletCircleMarkerStyleSchema>;

/**
 * Safely extracts style properties from Leaflet CircleMarker options
 * @param options - Leaflet CircleMarker options object
 * @returns Parsed style properties or undefined if invalid
 */
export function extractLeafletStyle(
  options: unknown
): LeafletCircleMarkerStyle | undefined {
  if (!options || typeof options !== "object") {
    return undefined;
  }

  const result = leafletCircleMarkerStyleSchema.safeParse(options);
  return result.success ? result.data : undefined;
}

/**
 * Base schema for internal feature properties added by frontend
 */
const baseInternalPropertiesSchema = z.object({
  _layerType: z.nativeEnum(GeoLayerType),
  _color: z.string(),
  _opacity: z.number(),
  _fillOpacity: z.number().optional(),
  _strokeColor: z.string(),
  _strokeWidth: z.number(),
});

/**
 * Availability layer internal properties schema
 */
export const availabilityLayerPropertiesSchema = baseInternalPropertiesSchema.extend({
  _layerType: z.literal(GeoLayerType.AVAILABILITY),
});

/**
 * Demand layer internal properties schema
 */
export const demandLayerPropertiesSchema = baseInternalPropertiesSchema.extend({
  _layerType: z.literal(GeoLayerType.DEMAND),
  _radius: z.number().optional(),
  _intensity: z.number().optional(),
});

/**
 * Boundary layer internal properties schema
 */
export const boundaryLayerPropertiesSchema = baseInternalPropertiesSchema.extend({
  _layerType: z.literal(GeoLayerType.BOUNDARY),
});

/**
 * Discriminated union for all layer type properties
 */
export const layerPropertiesSchema = z.discriminatedUnion("_layerType", [
  availabilityLayerPropertiesSchema,
  demandLayerPropertiesSchema,
  boundaryLayerPropertiesSchema,
]);

/**
 * Type for availability layer properties
 */
export type AvailabilityLayerProperties = z.infer<typeof availabilityLayerPropertiesSchema>;

/**
 * Type for demand layer properties
 */
export type DemandLayerProperties = z.infer<typeof demandLayerPropertiesSchema>;

/**
 * Type for boundary layer properties
 */
export type BoundaryLayerProperties = z.infer<typeof boundaryLayerPropertiesSchema>;

/**
 * Type for all layer properties (discriminated union)
 */
export type LayerProperties = z.infer<typeof layerPropertiesSchema>;

/**
 * Safely extracts internal layer properties from feature properties
 * @param properties - Feature properties object
 * @returns Parsed layer properties or undefined if invalid
 */
export function extractLayerProperties(
  properties: unknown
): LayerProperties | undefined {
  if (!properties || typeof properties !== "object") {
    return undefined;
  }

  const result = layerPropertiesSchema.safeParse(properties);
  return result.success ? result.data : undefined;
}

/**
 * Extracts display properties from layer properties
 * @param layerProps - Layer properties from discriminated union
 * @returns Display properties object
 */
export function getDisplayPropertiesFromLayer(
  layerProps: LayerProperties
): {
  color: string;
  opacity: number;
  fillOpacity?: number;
  strokeColor: string;
  strokeWidth: number;
  radius?: number;
} {
  return {
    color: layerProps._color,
    opacity: layerProps._opacity,
    fillOpacity: layerProps._fillOpacity,
    strokeColor: layerProps._strokeColor,
    strokeWidth: layerProps._strokeWidth,
    ...(layerProps._layerType === GeoLayerType.DEMAND && layerProps._radius !== undefined
      ? { radius: layerProps._radius }
      : {}),
  };
}
